// Where the employee works decides which policies answer them. Ask HR asks once, before their
// first answer: US or Canada, then the state for the US. The choice is stored and used on the
// web and in Slack; they can change it any time in Settings.

import { useEffect, useState } from "react";
import { api } from "../api.js";
import { US_STATES, type LocationSummary, type WorkLocation } from "@ask-hr/core/sources";

const STATES = Object.entries(US_STATES).sort((a, b) => a[1].localeCompare(b[1]));

const keyOf = (l: WorkLocation) => (l.country === "CA" ? "CA" : `US-${l.state}`);
const fromKey = (k: string): WorkLocation => (k === "CA" ? { country: "CA" } : { country: "US", state: k.slice(3) });

export function LocationPicker({ location, onChoose }: { location: LocationSummary; onChoose: (l: WorkLocation) => void }) {
  const chosen = location.source === "chosen" ? location.place : null;
  return (
    <>
      <label className="sr-only" htmlFor="work-location">
        Where you work
      </label>
      <select id="work-location" value={chosen ? keyOf(chosen) : ""} onChange={(e) => e.target.value && onChoose(fromKey(e.target.value))}>
        {chosen ? null : (
          <option value="" disabled>
            Not set
          </option>
        )}
        <option value="CA">Canada</option>
        <optgroup label="United States">
          {STATES.map(([code, name]) => (
            <option key={code} value={`US-${code}`}>
              {name}
            </option>
          ))}
        </optgroup>
      </select>
    </>
  );
}

// Settings > Where you work. Saved to their account (not this browser), so it applies on the web
// and in Slack.
export function LocationSetting() {
  const [location, setLocation] = useState<LocationSummary | null>(null);
  const [status, setStatus] = useState<"" | "saved" | "error">("");
  useEffect(() => {
    let live = true;
    api<{ location: LocationSummary }>("GET", "/api/me").then(
      (r) => live && setLocation(r.location),
      () => live && setStatus("error"),
    );
    return () => {
      live = false;
    };
  }, []);
  async function choose(place: WorkLocation) {
    try {
      setLocation((await api<{ location: LocationSummary }>("PUT", "/api/me/location", { place })).location);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }
  return (
    <div className="set-row">
      <span className="lbl">
        Where you work<small>Decides which policies answer you, on the web and in Slack</small>
      </span>
      <span>
        {location ? <LocationPicker location={location} onChoose={(l) => void choose(l)} /> : status === "error" ? null : "Loading…"}
        <span role="status" className="muted small">
          {status === "saved" ? " Saved" : status === "error" ? " Couldn't load or save your location. Try again." : ""}
        </span>
      </span>
    </div>
  );
}

export function LocationAsk({
  onChoose,
  question,
  hrPlace,
}: {
  onChoose: (l: WorkLocation) => void;
  question?: string | undefined; // shown when Ask HR is holding a question until they answer
  hrPlace: WorkLocation | null;
}) {
  const [step, setStep] = useState<"country" | "state">("country");
  const [state, setState] = useState(hrPlace?.country === "US" ? (hrPlace.state ?? "") : "");
  const hrSays = (country: WorkLocation["country"]) => (hrPlace?.country === country ? " (your HR record)" : "");

  return (
    <div className="location-ask" role="group" aria-labelledby="where-h">
      <p id="where-h">
        <strong>Where do you work?</strong>{" "}
        {question ? "I'll answer as soon as you pick. " : ""}
        Some policies differ by location, so this decides which ones answer you. You'll only be asked once; change it any time
        in Settings.
      </p>
      {question ? <p className="held-question prewrap">“{question}”</p> : null}
      {step === "country" ? (
        <div className="actions start">
          <button type="button" className={hrPlace?.country === "US" ? "primary" : undefined} onClick={() => setStep("state")}>
            United States{hrSays("US")}
          </button>
          <button type="button" className={hrPlace?.country === "CA" ? "primary" : undefined} onClick={() => onChoose({ country: "CA" })}>
            Canada{hrSays("CA")}
          </button>
        </div>
      ) : (
        <form
          className="actions start"
          onSubmit={(e) => {
            e.preventDefault();
            if (state) onChoose({ country: "US", state });
          }}
        >
          <label className="sr-only" htmlFor="where-state">
            State
          </label>
          <select id="where-state" value={state} onChange={(e) => setState(e.target.value)} autoFocus>
            <option value="" disabled>
              Choose your state
            </option>
            {STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
                {hrPlace?.state === code ? " (your HR record)" : ""}
              </option>
            ))}
          </select>
          <button type="submit" className="primary" disabled={!state}>
            Continue
          </button>
          <button type="button" onClick={() => setStep("country")}>
            Back
          </button>
        </form>
      )}
    </div>
  );
}
